import { Box, Typography, Card, Tooltip, TextField, Select, MenuItem } from "@mui/material";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import React, { FC, useState, useEffect, useCallback } from "react";
import { PencilIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers-pro";
import debounce from "lodash.debounce";
import i18next from "i18next";
import moment from "moment";
import dayjs from "dayjs";
import { Category } from "../enums/Category";

import ReportEntryActions from "./molecules/ReportEntryActions";
import ReportActionModal from "./molecules/ReportActionModal";
import { useReportActions } from "../hooks/useReportActions";
import { FilterReportEntries } from "../service/reportEntry";
import { formatCurrency } from "../utils/maskFormatMoney";
import { statusBackgroundColor } from "../enums/Status";
import { statusFilterOptions } from "../utils/filter";
import { ViewDocument } from "./atoms/ViewDocument";
import Collaborator from "../@types/Collaborator";
import BottomSheet from "./molecules/BottomSheet";
import StatusChip from "./molecules/StatusChip";
import { FilterValues } from "../@types/filter";
import { useUsers } from "../service/user";
import Loading from "./atoms/Loading";

interface RefundReportTableMobileProps {
  title: string;
  reportData: FilterReportEntries | undefined;
  filterValues: FilterValues | undefined;
  selfRefundsOnly: boolean;
  isAdvanceView?: boolean | null;
  loggedUser?: Collaborator;
  collaborators?: Collaborator[];
  isLoading?: boolean;
  onFilterUpdate: (filterValues: FilterValues) => void;
}

export const RefundReportTableMobile: FC<RefundReportTableMobileProps> = ({
  reportData,
  isAdvanceView,
  title,
  isLoading,
  filterValues,
  selfRefundsOnly,
  collaborators,
  loggedUser,
  onFilterUpdate,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [action, setAction] = useState("");
  const [payload, setPayload] = useState<{ id: number; refuseReason?: string; approveReason?: string }>({ id: 0 });

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [localFilterValues, setLocalFilterValues] = useState<FilterValues>({
    collaboratorName: "",
    collaborator: null,
    ownerName: "",
    costCenter: "",
    statusFilter: null,
    startDate: null,
    endDate: null,
    expenseCategory: null,
    ownerApproved: undefined,
    financialApproved: undefined,
    ownerApprovedDateUntil: null,
    ownerApprovedDateAfter: null,
    financialApproveDateUntil: null,
    financialApproveDateAfter: null,
    createDateUntil: null,
    createDateAfter: null,
  } as FilterValues);

  const navigate = useNavigate();
  const location = useLocation();
  const { onRequestAction } = useReportActions();
  const { data: users } = useUsers(filterValues, selfRefundsOnly);

  const handleClearFilters = () => {
    const newFilterValues = {
      ...filterValues,
      costCenter: "",
      statusFilter: null,
      startDate: null,
      endDate: null,
      expenseCategory: null,
      createDateUntil: null,
      createDateAfter: null,
      ownerApprovedDateUntil: null,
      ownerApprovedDateAfter: null,
      financialApproveDateUntil: null,
      financialApproveDateAfter: null,
      clientRefund: null,
    } as FilterValues;

    setLocalFilterValues(newFilterValues);
    onFilterUpdate(newFilterValues);
  };

  const handleFilterChange = (field: keyof FilterValues, value: unknown) => {
    const newFilterValues = {
      ...localFilterValues,
      [field]: value,
    } as FilterValues;

    // debounced because this filter is handled only by text filter
    if (!selfRefundsOnly) {
      debouncedFilterUpdate(newFilterValues);
    }
    setLocalFilterValues(newFilterValues);
  };

  const handleAction = () => {
    onRequestAction(action, payload, () => {
      setOpenModal(false);
      setAction("");
      setPayload({ id: 0 });
    });
  };

  const handleApproveReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPayload({ ...payload, approveReason: e.target.value });
  };

  const handleRefuseReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPayload({ ...payload, refuseReason: e.target.value });
  };

  const handleClose = () => {
    setAction("");
    setPayload({ id: 0 });
  };

  const onApplyFilters = useCallback(() => {
    onFilterUpdate(localFilterValues);
  }, [localFilterValues, onFilterUpdate]);

  const debouncedFilterUpdate = useCallback(
    debounce((newFilterValues: FilterValues) => {
      onFilterUpdate(newFilterValues);
    }, 300),
    [onFilterUpdate],
  );

  useEffect(() => {
    setLocalFilterValues(filterValues as FilterValues);
  }, [filterValues]);

  return (
    <div className="w-full px-4 pb-10">
      <Box sx={{ width: "100%", paddingBottom: 6 }}>
        <ReportActionModal
          isOpen={openModal}
          setIsOpen={setOpenModal}
          onClose={handleClose}
          action={action as "approve" | "refuse" | "approvePerManager" | "sendToApproval"}
          onConfirm={handleAction}
          onRefuseReasonChange={handleRefuseReasonChange}
          onApproveReasonChange={handleApproveReasonChange}
          approveReason={payload.approveReason}
          refuseReason={payload.refuseReason}
        />

        <section className="sticky top-0 z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-white backdrop-blur-sm -z-10" />

            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

            <div className="flex justify-between items-center py-2">
              <Typography variant="h6" sx={{ color: "#0C7878", fontWeight: "bold", textAlign: "start" }}>
                {title}
              </Typography>
              {selfRefundsOnly && (
                <div className="flex items-center px-3 py-1.5 bg-[#F8FAFA] rounded-lg border border-[#E5E7EB] h-8">
                  <span className="text-gray-500 text-xs mr-1.5 leading-none">Total:</span>
                  <span className="text-[#0C7878] font-semibold text-sm leading-none">
                    {formatCurrency(
                      Number(users?.content[0]?.[isAdvanceView ? "totalAdvanceValue" : "totalValue"] || 0),
                      !!isAdvanceView,
                    )}
                  </span>
                </div>
              )}

              {selfRefundsOnly && (
                <div className="flex items-center gap-2">
                  <PlusIcon
                    onClick={() => navigate(`/new_${isAdvanceView ? "advance_payment" : "request"}`)}
                    fill="#0C7878"
                    className="w-7 h-7 cursor-pointer hover:opacity-80 transition-opacity"
                  />

                  <AdjustmentsHorizontalIcon
                    onClick={() => setIsBottomSheetOpen(true)}
                    stroke="#0C7878"
                    className="w-7 h-7 cursor-pointer"
                  />
                </div>
              )}

              {!selfRefundsOnly && localFilterValues?.collaborator?.id && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFilterChange("collaborator", null)}
                    className="flex items-center gap-2 text-[#0C7878] hover:text-[#0C7878]/80 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Voltar
                    </Typography>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {!filterValues?.collaborator?.id && (
          <section className="my-4">
            <div className="space-y-2">
              <Typography variant="subtitle2" sx={{ color: "#6B7280", fontWeight: 500 }}>
                Selecione um colaborador
              </Typography>
              <div
                className="grid grid-cols-1 gap-2"
                style={{
                  height: "600px",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                {collaborators?.map((collaborator) => (
                  <div
                    key={collaborator.id}
                    onClick={() => handleFilterChange("collaborator", collaborator)}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-[#0C7878] cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0C7878]/10 flex items-center justify-center">
                        <span className="text-[#0C7878] font-medium text-sm">
                          {collaborator.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {collaborator.name}
                        </Typography>
                      </div>
                    </div>
                    <span className="text-[#0C7878] font-semibold text-sm">
                      {formatCurrency(Number(collaborator.totalValue))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {filterValues?.collaborator?.id && !selfRefundsOnly && (
          <section className="my-4">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#0C7878]/10 flex items-center justify-center">
                  <span className="text-[#0C7878] font-medium text-sm">
                    {filterValues.collaborator.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {filterValues.collaborator.name}
                  </Typography>
                </div>
              </div>
              <span className="text-[#0C7878] font-semibold text-sm">
                {formatCurrency(
                  Number(
                    collaborators?.find((user: Collaborator) => user.id === filterValues?.collaborator?.id)
                      ?.totalValue ||
                      users?.content.find((user: Collaborator) => user.id === filterValues?.collaborator?.id)
                        ?.totalValue ||
                      0,
                  ),
                )}
              </span>
            </div>
          </section>
        )}

        {isLoading && <Loading />}

        {reportData?.content.map((data) => (
          <React.Fragment key={data.id}>
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#FFFFFF",
                  width: "98%",
                  borderRadius: "16px",
                  border: "1px solid rgba(12, 120, 120, 0.3)",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  overflow: "hidden",
                }}
              >
                {/* Cabeçalho do Card */}
                <div className="p-4 flex justify-between items-center border-b border-gray-100">
                  <div className="flex flex-col">
                    <Typography fontSize="22px" fontWeight="bold" color="#0C7878" className="tracking-tight">
                      {formatCurrency(Number(data.value))}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", textAlign: "start" }} color="text.secondary">
                      {moment(data.expenseDate).format("DD/MM/YYYY")}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-3">
                    <StatusChip data={data} />
                  </div>
                </div>

                {/* Conteúdo do Card */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-300 rounded-full" />
                    <Typography sx={{ fontSize: "13px" }} className="flex items-center gap-1 min-w-0">
                      <span className="text-[#0C7878] opacity-90 shrink-0">Cliente:</span>
                      <Tooltip title={`${data.costCenter.client} - ${data.costCenter.name}`}>
                        <span className="font-medium truncate">
                          {data.costCenter.client} - {data.costCenter.name}
                        </span>
                      </Tooltip>
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-300 rounded-full" />
                    <Typography sx={{ fontSize: "13px" }}>
                      <span className="text-[#0C7878] opacity-90">Gestor:</span>{" "}
                      <span className="font-medium">{data.costCenter.owner}</span>
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-300 rounded-full" />
                    <Typography sx={{ fontSize: "13px" }}>
                      <span className="text-[#0C7878] opacity-90">Categoria:</span>{" "}
                      <span className={`${data.expendCategory === Category.advance ? "font-bold" : "font-medium"}`}>
                        {i18next.t(`category.${data.expendCategory}`)}
                        {data.expendSubcategory && ` - ${i18next.t(`sub_category.${data.expendSubcategory}`)}`}
                      </span>
                    </Typography>
                  </div>
                </div>

                {/* Rodapé do Card */}
                <div className="px-4 py-3 bg-gray-50 flex justify-between">
                  <div className="flex gap-2">
                    <ReportEntryActions
                      data={data}
                      loggedUser={loggedUser}
                      onApprove={() => {
                        setAction("approve");
                        setPayload({ id: data.id, approveReason: data.financialApprovedReason || "" });
                        onRequestAction(
                          "approve",
                          { id: data.id, approveReason: data.financialApprovedReason || "" },
                          () => {
                            setOpenModal(false);
                            setAction("");
                            setPayload({ id: 0 });
                          },
                        );
                      }}
                      onApprovePerManager={() => {
                        setAction("approvePerManager");
                        setPayload({ id: data.id, approveReason: data.ownerApprovedReason || "" });
                        setOpenModal(true);
                      }}
                      onRefuse={() => {
                        setAction("refuse");
                        setPayload({ id: data.id });
                        setOpenModal(true);
                      }}
                      onReturnToApproval={() => {
                        setAction("sendToApproval");
                        setPayload({ id: data.id });
                        setOpenModal(true);
                      }}
                      onEdit={() => {
                        if (location.pathname === "/advance") {
                          navigate(`/edit_advance?id=${data.id}`);
                        } else {
                          navigate(`/edit_request?id=${data.id}`);
                        }
                      }}
                      onDelete={() => {
                        // TODO: Implementar ação de deletar
                      }}
                      onMarkAsPaid={() => {
                        setAction("markAsPaid");
                        setPayload({ id: data.id });
                        setOpenModal(true);
                      }}
                      onConfirmReceipt={() => {
                        navigate(`/confirm_advance_receipt?id=${data.id}`);
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <ViewDocument reportEntry={data} isMobile />
                    <Tooltip title={location.pathname === "/advance" ? "Editar Adiantamento" : "Editar Solicitação"}>
                      <PencilIcon
                        fill="#6B7280"
                        className="w-5 h-5 text-gray-500 cursor-pointer hover:text-[#0C7878] transition-colors"
                        onClick={() => {
                          if (location.pathname === "/advance") {
                            navigate(`/edit_advance?id=${data.id}`);
                          } else {
                            navigate(`/edit_request?id=${data.id}`);
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </Card>
            </Box>
          </React.Fragment>
        ))}

        <BottomSheet
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          onApply={() => {
            onApplyFilters();
            setIsBottomSheetOpen(false);
          }}
          title="Filtros"
        >
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-[#0C7878] rounded-full" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#374151" }}>
                  Centro de Custo
                </Typography>
              </div>
              <TextField
                fullWidth
                size="small"
                placeholder="Digite o centro de custo"
                value={localFilterValues?.costCenter || ""}
                onChange={(e) => handleFilterChange("costCenter", e.target.value)}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#0C7878",
                    },
                  },
                }}
              />
            </div>

            {/* Seção Status */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-[#0C7878] rounded-full" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#374151" }}>
                  Status
                </Typography>
              </div>
              <Select
                fullWidth
                size="small"
                value={localFilterValues?.statusFilter?.key || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "statusFilter",
                    statusFilterOptions[e.target.value as keyof typeof statusFilterOptions],
                  )
                }
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                }}
              >
                <MenuItem value="">Todos os status</MenuItem>
                {Object.keys(statusFilterOptions).map((key) => (
                  <MenuItem
                    key={key}
                    value={key}
                    sx={{
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                      gap: 2,
                      pl: 2,
                      "& .MuiMenuItem-root": {
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                      },
                    }}
                  >
                    <div
                      className="w-2 h-2 rounded-full inline-flex items-center mr-2"
                      style={{
                        backgroundColor: statusBackgroundColor(statusFilterOptions[key].status!),
                      }}
                    />
                    {statusFilterOptions[key]?.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Seção Data */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-[#0C7878] rounded-full" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#374151" }}>
                  Período
                </Typography>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col items-start gap-1">
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Data Inicial
                  </Typography>
                  <DatePicker
                    value={dayjs(localFilterValues?.startDate) || ""}
                    onChange={(date) => handleFilterChange("startDate", date?.format("YYYY-MM-DD") ?? null)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: false,
                        fullWidth: true,
                        placeholder: "DD/MM/YYYY",
                        sx: { minWidth: { xs: "100%", sm: "160px" } },
                      },
                      actionBar: { actions: ["clear"] },
                    }}
                  />
                </div>

                <div className="flex flex-col items-start gap-1">
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Data Final
                  </Typography>
                  <DatePicker
                    value={dayjs(localFilterValues?.endDate) || ""}
                    onChange={(date) => handleFilterChange("endDate", date?.format("YYYY-MM-DD") ?? null)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: false,
                        fullWidth: true,
                        placeholder: "DD/MM/YYYY",
                        sx: { minWidth: { xs: "100%", sm: "160px" } },
                      },
                      actionBar: { actions: ["clear"] },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Botão de limpar */}
            <div className="pt-4 mt-auto border-t border-gray-100">
              <button
                onClick={handleClearFilters}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        </BottomSheet>
      </Box>
    </div>
  );
};
